import React from 'react';
import Helmet from 'react-helmet';
import { useStaticQuery, graphql } from 'gatsby';
import { FACEBOOK_DOMAIN_VERIFICATION_CODE } from '../constants';

interface QueryData {
  sanityWebsite: {
    metadata: {
      title: string;
      description: string;
      url: string;
      image: {
        asset: {
          url: string;
        };
      };
    };
  };
}

interface SEOProps {
  title?: string;
  description?: string;
  urlPath?: string;
  imageUrl?: string;
  meta?: Array<
    | { name: string; content: any; property?: undefined }
    | { property: string; content: any; name?: undefined }
  >;
}

function SEO({ title, description, urlPath, imageUrl, meta = [] }: SEOProps) {
  const data = useStaticQuery<QueryData>(
    graphql`
      {
        sanityWebsite {
          metadata {
            title
            description
            url
            image {
              asset {
                url
              }
            }
          }
        }
      }
    `,
  );

  const metadata = data.sanityWebsite.metadata;

  title = title ? title + ' - ' + metadata.title : metadata.title;
  description = description || metadata.description;
  const url = metadata.url.replace(/\/+$/g, '') + (urlPath || '/');
  imageUrl = imageUrl || metadata.image.asset.url + '?w=1200&h=630&fit=min';

  const metaList = [
    {
      property: 'og:type',
      content: 'website',
    },
    {
      name: 'twitter:card',
      content: 'summary_large_image',
    },
  ].concat(meta);

  if (title) {
    metaList.push(
      ...[
        {
          property: 'og:title',
          content: title,
        },
        {
          name: 'twitter:title',
          content: title,
        },
      ],
    );
  }

  if (description) {
    metaList.push(
      ...[
        {
          name: 'description',
          content: description,
        },
        {
          property: 'og:description',
          content: description,
        },
        {
          name: 'twitter:description',
          content: description,
        },
      ],
    );
  }

  if (url) {
    metaList.push({
      property: 'og:url',
      content: url,
    });
  }

  if (imageUrl) {
    metaList.push(
      ...[
        {
          property: 'og:image',
          content: imageUrl,
        },
        {
          property: 'twitter:image',
          content: imageUrl,
        },
      ],
    );
  }

  metaList.push(
    ...[
      {
        name: 'author',
        content: 'Mosca Digital',
      },
      {
        name: 'twitter:creator',
        content: 'Mosca Digital',
      },
    ],
  );

  if (FACEBOOK_DOMAIN_VERIFICATION_CODE) {
    metaList.push({
      name: 'facebook-domain-verification',
      content: FACEBOOK_DOMAIN_VERIFICATION_CODE,
    });
  }

  return <Helmet title={title} meta={metaList} />;
}

export default SEO;
